import { observer } from "mobx-react-lite";
import { useContext } from "react";
import { useField, useForm, useFormState } from "react-final-form";

import { Heading, Link, Stack } from "@bps/fluent-ui";
import { Labels } from "@modules/practice/screens/shared-components/types/labels.enums.types.ts";
import { PatientCardIds } from "@modules/practice/screens/shared-components/types/patient-card-ids.enum.ts";
import { CommunicationFieldValue } from "@shared-types/practice/communication-field-value.type.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { ContactFetcher } from "@ui-components/ContactFetcher.tsx";
import { FieldSpy } from "@ui-components/form/FieldSpy.tsx";

import { PatientDemographicContext } from "../../../context/PatientDemographicContext.ts";
import {
  contactFormNameOf,
  PatientEditFormValues,
  patientFormNameOf
} from "../PatientEditFormValues.tsx";
import { AddContactMethodsButton } from "./AddContactMethodsButton.tsx";
import { ChooseOrganisationDialog } from "./ChooseOrganisationDialog.tsx";
import { ContactMethodAddress } from "./ContactMethodAddress.tsx";
import { ContactMethodCommunications } from "./ContactMethodCommunications.tsx";
import { LinkedEmployerContactMethods } from "./LinkedEmployerContactMethods.tsx";
import { onChangeCommunications } from "./utils.ts";

type EditContactMethodsProps = {
  contact?: Contact;
  onRemoveCommunication?: (
    index: number,
    values: PatientEditFormValues
  ) => void;
};

export const EditContactMethods: React.FC<EditContactMethodsProps> = observer(
  ({ contact, onRemoveCommunication }) => {
    const reactFinalForm = useForm<PatientEditFormValues>();
    const { initialValues, values } = useFormState<PatientEditFormValues>();

    const {
      isEmployerContactMethodsVisible,
      setEmployerContactMethodsVisible
    } = useContext(PatientDemographicContext);

    const {
      input: { value: isHealthProvider }
    } = useField<boolean | undefined>(contactFormNameOf("isHealthProvider"), {
      subscription: { value: true }
    });

    const {
      input: { value: linkedOrganisationContactIds }
    } = useField<PatientEditFormValues["linkedOrganisationContactIds"]>(
      patientFormNameOf("linkedOrganisationContactIds"),
      {
        subscription: { value: true }
      }
    );

    return (
      <div
        id={`${PatientCardIds.contactMethods}-edit`}
        style={{ paddingRight: 16 }}
      >
        <Stack
          styles={(_props, theme) => {
            return {
              root: {
                borderBottom: "1px solid",
                borderColor: theme.palette.neutralLighterAlt
              }
            };
          }}
        >
          <Stack
            horizontal
            horizontalAlign="space-between"
            styles={{ root: { lineHeight: "22px", marginBottom: 6 } }}
          >
            <Heading labelPaddings variant="section-heading-light">
              {Labels.contactMethods}
            </Heading>
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <Link onClick={() => setEmployerContactMethodsVisible(true)}>
                Get from organisation
              </Link>
              {isEmployerContactMethodsVisible && contact && (
                <ChooseOrganisationDialog
                  onDismiss={() => setEmployerContactMethodsVisible(false)}
                  contact={contact}
                />
              )}
              <AddContactMethodsButton
                reactFinalForm={reactFinalForm}
                formValues={values}
              />
            </Stack>
          </Stack>
          {contact &&
            linkedOrganisationContactIds?.map(id => (
              <ContactFetcher contactId={id} key={id}>
                {emp => {
                  const linkedOrgIsEmployer =
                    contact.employerContacts.includes(emp);

                  return (
                    <LinkedEmployerContactMethods
                      messageHeader={
                        linkedOrgIsEmployer
                          ? `From employer ${emp.name}`
                          : `From ${emp.name}`
                      }
                      contact={emp}
                      index={linkedOrganisationContactIds?.findIndex(
                        x => x === emp.id
                      )}
                    />
                  );
                }}
              </ContactFetcher>
            ))}
          <ContactMethodCommunications
            contact={contact}
            name={patientFormNameOf("communications")}
            onRemove={onRemoveCommunication}
            disableSendForm={isHealthProvider}
          />
          <FieldSpy
            name={patientFormNameOf("communications")}
            onChange={(value: CommunicationFieldValue[]) => {
              onChangeCommunications(
                value,
                { initialValues, values, isHealthProvider: !!isHealthProvider },
                reactFinalForm
              );
            }}
          />
          <ContactMethodAddress name={patientFormNameOf("addresses")} />
        </Stack>
      </div>
    );
  }
);
